//
//  groupConfigurationNavigation.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines shared Group Configuration cards and submenu destinations.
//

import { Permission } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import { LinkPermissionMode, type ILayoutSubMenu } from "src/contexts/layouts/LayoutContext";

import {
    toSettingsNavigationLayoutLink,
    type ISettingsNavigationDestination
} from "./settingsNavigation";

/** Builds the Group Configuration destinations for the supplied Group. */
export const createGroupConfigurationDestinations = (
    groupId: number
): ISettingsNavigationDestination[] => [
    {
        label: "Trial Group Item Templates",
        href: `/groups/${groupId}/configuration/items`,
        permissionMode: LinkPermissionMode.Any,
        permissions: [Permission.TrialGroupItemTemplatesView],
        card: {
            description: "Configure the item templates available to this Group.",
            icon: AppSettings.icons.models.item
        }
    }
];

/** Projects the shared destinations into the Group Configuration submenu. */
export const createGroupConfigurationSubMenu = (groupId: number): ILayoutSubMenu => ({
    code: "group-configuration",
    title: "Configuration",
    context: { groupId },
    items: createGroupConfigurationDestinations(groupId).map(toSettingsNavigationLayoutLink)
});

/** Provides the card-page title, description and permission metadata. */
export const createGroupConfigurationSection = (
    groupId: number
): ISettingsNavigationDestination => ({
    label: "Configuration",
    title: "Group Configuration",
    href: `/groups/${groupId}/configuration`,
    description: "Manage settings that apply only to this Group.",
    permissionMode: LinkPermissionMode.Any,
    permissions: [Permission.TrialGroupItemTemplatesView],
    card: {
        description: "Manage settings that apply only to this Group.",
        icon: AppSettings.icons.settings
    },
    children: createGroupConfigurationDestinations(groupId)
});
